import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import { Eye, CheckCircle, XCircle, Download } from 'lucide-react';

const statusFilters = [
  { value: '', label: 'All Status' }, { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' }, { value: 'REJECTED', label: 'Rejected' },
];

export default function AdminKycPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['adminKyc', statusFilter, page],
    queryFn: () => api.get('/admin/kyc', { params: { status: statusFilter, page, limit: 20 } }).then(r => r.data),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/kyc/${id}/approve`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminKyc'] }); setSelected(null); },
  });
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.patch(`/admin/kyc/${id}/reject`, { reason }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminKyc'] }); setSelected(null); setRejectReason(''); },
  });

  const documents = data?.data || [];
  const meta = data?.meta || { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white">KYC Management</h1><p className="text-sm text-slate-400">{meta.total} documents</p></div>
        <Button variant="secondary" size="sm"><Download className="w-4 h-4 mr-1" />Export</Button>
      </div>
      <Card><CardContent className="py-3">
        <Select options={statusFilters} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} />
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-white/[0.06] bg-white/[0.02]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">User</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Document</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-white/[0.04]">
              {isLoading ? <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Loading...</td></tr> :
                documents.length === 0 ? <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No documents</td></tr> :
                documents.map((doc: any) => (
                  <tr key={doc.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3"><p className="text-sm font-medium text-white">{doc.user?.profile?.fullName || doc.user?.username}</p><p className="text-xs text-slate-500">@{doc.user?.username}</p></td>
                    <td className="px-4 py-3 text-sm text-slate-300">{doc.type}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{doc.fileName}</td>
                    <td className="px-4 py-3"><Badge status={doc.status} /></td>
                    <td className="px-4 py-3 text-sm text-slate-400">{new Date(doc.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setSelected(doc)}><Eye className="w-3.5 h-3.5" /></Button>
                        {doc.status === 'PENDING' && <>
                          <Button variant="ghost" size="sm" onClick={() => approveMutation.mutate(doc.id)}><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => { setSelected(doc); setRejectReason(''); }}><XCircle className="w-3.5 h-3.5 text-red-400" /></Button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="KYC Document">
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Type</p><p className="font-medium text-white">{selected.type}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Status</p><Badge status={selected.status} /></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">File</p><p className="font-medium text-white">{selected.fileName}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Submitted</p><p className="font-medium text-white">{new Date(selected.createdAt).toLocaleString()}</p></div>
            </div>
            {selected.status === 'PENDING' && (
              <div className="flex gap-2 pt-3 border-t border-white/[0.06]">
                <Button onClick={() => approveMutation.mutate(selected.id)} isLoading={approveMutation.isPending}><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                <div className="flex-1"><input type="text" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Rejection reason..."
                  className="w-full px-3 py-2 border border-white/[0.06] rounded-lg text-sm bg-white/[0.02] text-white placeholder-slate-500" /></div>
                <Button variant="danger" onClick={() => rejectMutation.mutate({ id: selected.id, reason: rejectReason })} isLoading={rejectMutation.isPending}>Reject</Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
